import PhoneInput from "../../components/PhoneInput";
import { toPhoneDigits } from "../../helper/phoneUtils";
import { Datepicker, MbscCalendarEvent } from "@mobiscroll/react";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import {
  createEligiblesApi,
  createPatientApi,
} from "../../api-helpers/PatientApiHelper";
import { showToast } from "../../store/slices/toastSlice";
import {
  Contracts,
  EligiblesDto,
  SavePatientRequest,
  AddExternalContact,
} from "patients-experience-client";
import { IdValuePairDTO, Lookup } from "common-util-experience-client";
import {
  createCommonUtilsApi,
  createLocationApi,
} from "../../api-helpers/CommonUtilsApiHelper";
import Lookups from "../../constants/Lookups";
import { DataListType } from "../../components/AsyncTypeahead";
import { ToastConfig } from "../../components/Toaster";
import { DesignerIdConstants } from "../../helper/WorkflowConstants";
import { workflowStatesCache } from "../../grid/renderers/WorkflowStateCellValueGetter";
import Spinner from "../../components/Spinner";
import "../../assets/scss/custom/_custom.scss";
import { formatDateTask } from "../../helper/dateFormatter";
import ScriptModal from "./ScriptModal";
import { EligiblePatient } from "../Eligibility/EligibilityGrid";
import { Genders as genders } from "../../constants/Genders";
import { ELIGIBILITY_STATES_ID } from "../../constants/UiStrings";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import {
  AssignFormRequest,
  MemberFormInfo,
  AdditionalAttributeInfo,
  FormAnswersDetails,
} from "clinical-forms-experience-client";
import {
  formatDateToMMDDYYYY,
  formatTimeToHHMM,
  roundToNearest15Minutes,
} from "../../helper/dateFormatter";
import { External_Care_Team } from "../../constants/Lookups";
import { Section, ReadField, SectionNavLayout } from "../../components/FormSections";

type AddPatientProps = {
  eligiblePatient?: EligiblePatient;
  onSubmitPatient: (toasterConfig: ToastConfig) => void;
  setOffCanvasShow: Dispatch<SetStateAction<boolean>>;
  reloadGrid: () => void;
};

const AddPatient: React.FC<AddPatientProps> = ({
  eligiblePatient,
  onSubmitPatient,
  setOffCanvasShow,
  reloadGrid,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const patientApi = createPatientApi();
  const commonUtilApi = createCommonUtilsApi();
  const locationApi = createLocationApi();
  const eligiblesApi = createEligiblesApi();
  const clinicalformsApi = createClinicalFormsApi();

  const [validated, setValidated] = useState(false);
  const [contracts, setContracts] = useState<Contracts[] | undefined>([]);
  const [selectedState, setSelectedState] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [cityList, setCityList] = useState<IdValuePairDTO[]>([]);
  const [savePatientForm, setSavePatientForm] = useState<SavePatientRequest>(
    {} as SavePatientRequest,
  );
  const [languages, setLanguages] = useState<Lookup[]>([]);
  const [mailingStates, setMailingStates] = useState<Array<DataListType>>([]);
  const [race, setRace] = useState<Lookup[]>([]);
  const [ethinicity, setEthinicity] = useState<Lookup[]>([]);
  const [loading, setloading] = useState(false);
  const [invalidZipCode, setInvalidZipCode] = useState<boolean>(false);
  const [modalShow, setModalShow] = useState(false);
  const [openPicker, setOpenPicker] = useState<boolean>(false);
  const externalCareTeamLookup = External_Care_Team.externalTeamLookups;

  const [error, setError] = useState({
    dateOfBirth: false,
  });

  useEffect(() => {
    getContracts();
    loadLanguages();
  }, []);

  useEffect(() => {
    if (error.dateOfBirth) {
      const label = document.querySelector('label[for="dateOfBirth"]');
      if (label) {
        const input = label.nextElementSibling?.querySelector(
          "input.mbsc-textfield",
        );
        if (input) {
          input.classList.add("invalid-input");
        }
      }
    }
  }, [error]);

  useEffect(() => {
    const { stateId, cityId, zipCode } = eligiblePatient || {};

    if (stateId && cityId) {
      fetchState(stateId);
      fetchCity(stateId, cityId);
    } else if (stateId) {
      fetchState(stateId);
    } else if (zipCode) {
      fetchZipcodeDetails(zipCode);
    }

    if (eligiblePatient) {
      setSavePatientForm({
        ...savePatientForm,
        firstName: eligiblePatient.firstName || "",
        lastName: eligiblePatient.lastName || "",
        middleName: "",
        dateOfBirth: eligiblePatient.dateOfBirth || null, // Ensure dateOfBirth is null if not provided
        email: eligiblePatient.email || "",
        phone: toPhoneDigits(eligiblePatient.phoneNumber),
        contractId: eligiblePatient.contractId || "",
        marketId: eligiblePatient.marketId || undefined,
        mailingAddress: `${eligiblePatient.addressLine1 || ""} ${eligiblePatient.addressLine2 || ""}`,
        stateId: eligiblePatient.stateId || "",
        cityId: eligiblePatient.cityId || "",
        zipcode: eligiblePatient.zipCode || "",
        eligibleId: eligiblePatient.id || "",
        genderId: eligiblePatient.genderId || "",
        pathwayDesignerStateId: eligiblePatient.pathwayDesignerStateId || undefined,
        currentPathway:
          workflowStatesCache.get(eligiblePatient.pathwayDesignerStateId || "") || undefined,
        currentDesigner: eligiblePatient.pathwayDesignerStateId
          ? DesignerIdConstants.PATHWAY
          : undefined,
      });
    }
  }, [eligiblePatient]);

  const handleOffCanvasShow = () => {
    setModalShow(true);
  };

  const getContracts = async () => {
    const contracts = await patientApi.patientsControllerGetAllContracts();

    setContracts(contracts.data);
  };

  const loadLanguages = async () => {
    setloading(true);
    setLanguages([]);
    const response = await commonUtilApi.lookupControllerFindAll(
      Lookups.LANGUAGE_OPTIONS,
    );
    const race = await commonUtilApi.lookupControllerFindAll(
      Lookups.RACE_CATEGORY,
    );
    const ethinicity = await commonUtilApi.lookupControllerFindAll(
      Lookups.ETHNICITY_CATEGORY,
    );

    setLanguages(response.data);
    setRace(race.data);
    setEthinicity(ethinicity.data);
    setloading(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === "zipcode") {
      setInvalidZipCode(false);
      const zipcodeInput = document.querySelector(
        '[name="zipcode"]',
      ) as HTMLInputElement;
      zipcodeInput.setCustomValidity("");

      if (value.length === 5) {
        fetchZipcodeDetails(value);
      } else {
        setSavePatientForm((prevForm) => ({
          ...prevForm,
          stateId: "",
          cityId: "",
        }));
        setSelectedCity("");
        setSelectedState("");
        setCityList([]);
      }
    }

    setSavePatientForm({
      ...savePatientForm,
      [name]: value,
    });
  };

  const fetchCity = async (stateId: string, cityId: string) => {
    try {
      const { data } = await locationApi.locationControllerFindCity(
        stateId,
        cityId,
      );
      setSelectedCity(data.value);
    } catch (error) {
      console.error("Error fetching city :", error);
    }
  };

  const fetchState = async (stateId: string) => {
    try {
      const { data } = await locationApi.locationControllerFindState(stateId);
      setSelectedState(data.value);
    } catch (error) {
      console.error("Error fetching state :", error);
    }
  };

  const fetchZipcodeDetails = async (zipcode: string) => {
    try {
      setloading(true);
      setInvalidZipCode(false);
      const response =
        await locationApi.locationControllerGetZipcodeDetails(zipcode);
      // Assuming the API returns city, state, and other details
      const { state, cities } = response.data;
      setSelectedState(state.value);
      setSavePatientForm((prevForm) => ({
        ...prevForm,
        stateId: state.id,
      }));
      if (cities.length === 1) {
        setSelectedCity(cities[0].value);
        setCityList([]);
        setSavePatientForm((prevForm) => ({
          ...prevForm,
          cityId: cities[0].id,
        }));
      } else if (cities.length > 1) {
        setCityList(cities);
        setSavePatientForm((prevForm) => ({
          ...prevForm,
          cityId: "",
        }));
      } else {
        // Handle case where no cities are returned
        console.error("No cities found for this zipcode");
      }
    } catch (error) {
      console.error(error);
      setCityList([]);
      setInvalidZipCode(true);
    } finally {
      setloading(false);
    }
  };
  const dateRangeChangeHandlar = (name: string, event: MbscCalendarEvent) => {
    const selectedDate = new Date(event.value);
    const utcDate = new Date(
      Date.UTC(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
      ),
    );

    setSavePatientForm({
      ...savePatientForm,
      [name]: utcDate,
    });

    if (event.value) {
      setError({
        ...error,
        [name]: false,
      });
    }
  };

  const calculateMinDateFor18Years = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today;
  };
  const maxDate18YearsAgo = calculateMinDateFor18Years();

  const submitHandler = async (event: any) => {
    const phoneInput = document.querySelector(
      '[name="phone"]',
    ) as HTMLInputElement;
    const emailInput = document.querySelector(
      '[name="email"]',
    ) as HTMLInputElement;
    phoneInput.setCustomValidity("");
    emailInput.setCustomValidity("");
    event.preventDefault();
    setValidated(true);

    if (savePatientForm?.phone?.length !== 10) {
      phoneInput.setCustomValidity("Invalid Phone Number");
      return;
    }

    if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailInput.value)
    ) {
      emailInput.setCustomValidity("Invalid Email");
      return;
    }

    const zipcodeInput = document.querySelector(
      '[name="zipcode"]',
    ) as HTMLInputElement;

    if (zipcodeInput.value.length !== 5) {
      zipcodeInput.setCustomValidity("Invalid Zipcode");
      setInvalidZipCode(true);
      return;
    }

    if (
      zipcodeInput.value.length &&
      (savePatientForm.stateId === undefined ||
        savePatientForm.stateId === "") &&
      (savePatientForm.cityId === undefined || savePatientForm.cityId === "")
    ) {
      zipcodeInput.setCustomValidity("Invalid Zipcode");
    } else {
      zipcodeInput.setCustomValidity("");
    }

    if (invalidZipCode && savePatientForm.zipcode !== "") {
      zipcodeInput.setCustomValidity("Invalid Zipcode");
    } else {
      zipcodeInput.setCustomValidity("");
    }

    const form = event.currentTarget;
    if (
      savePatientForm.zipcode === "zipcode" &&
      savePatientForm.zipcode.length !== 5
    ) {
      return;
    }
    if (!form.checkValidity()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (error.dateOfBirth) {
      return;
    }

    try {
      setloading(true);

      let patientsData =
        await patientApi.patientsControllerCreate(savePatientForm);
      if (savePatientForm.eligibleId) {
        await eligiblesApi.eligiblesControllerUpdateEligibles(
          { statusId: ELIGIBILITY_STATES_ID.ENROLLED },
          savePatientForm.eligibleId,
        );

        if (savePatientForm.notes) {
          const clinicalForms =
            await clinicalformsApi.clinicalFormsControllerGetClinicalForms(
              ClinicalFormsConstants.careNoteType,
            );

          const patientEnrollmentCareNote = clinicalForms.data.filter(
            (form) =>
              form.title === ClinicalFormsConstants.patientEnrollmentCareNote,
          );
          const request: AssignFormRequest = {
            formId: patientEnrollmentCareNote[0].id,
            source: "manual",
            sourceRefId: savePatientForm.eligibleId,
          };

          const memberFormRef: MemberFormInfo | undefined = (
            await clinicalformsApi.clinicalFormsControllerAssignClinicalForm(
              request,
              patientsData.data,
            )
          ).data;

          const closest15Minutes = roundToNearest15Minutes();
          const timezoneOffset = closest15Minutes.getTimezoneOffset();

          const offsetInMs = timezoneOffset * 60 * 1000;
          const utcDateTime = new Date(closest15Minutes.getTime() + offsetInMs);

          const formattedDate = formatDateToMMDDYYYY(utcDateTime);
          const formattedTime = formatTimeToHHMM(utcDateTime);

          const otherAnswers: AdditionalAttributeInfo[] = [
            { name: "serviceDate", value: formattedDate },
            { name: "serviceTime", value: formattedTime },
          ];
          const saveFormAnswersRequest: FormAnswersDetails = {
            formAnswers: [],
            additionalAnswers: otherAnswers,
            comments: savePatientForm.notes,
          };

          await clinicalformsApi.clinicalFormsControllerSaveFormResponses(
            saveFormAnswersRequest,
            memberFormRef!.id,
          );
        }

        const externalCareTeamForm: AddExternalContact = {
          contactTypeId: externalCareTeamLookup.contactTypeId,
          credential: "",
          firstName: eligiblePatient?.pcpFirstName || " ",
          lastName: eligiblePatient?.pcpLastName || " ",
          npi: "",
          phone: "",
          phoneTypeId: externalCareTeamLookup.phoneTypeId,
          primaryTaxonomy: "",
          secondaryTaxonomy: "",
          specialtyId: externalCareTeamLookup.specialityId,
        };

        if (eligiblePatient?.pcpFirstName || eligiblePatient?.pcpLastName) {
          await patientApi.patientsControllerCreatePatientExternalContact(
            externalCareTeamForm,
            patientsData.data,
          );
        }
      }
      dispatch(showToast({
        type: "success",
        title: "Patient added successfully!",
        message: "",
        icon: "check_circle",
      }));
      navigate(`/patients/${patientsData.data}/appointments`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message?.message?.message
        ?? error.response?.data?.message?.message
        ?? error.response?.data?.message
        ?? "An error occurred while adding the patient";
      dispatch(showToast({
        type: "danger",
        title: errorMessage,
        message: "",
        icon: "error_outline",
      }));
    } finally {
      setloading(false);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setSavePatientForm((prevForm) => ({
      ...prevForm,
      [name]: checked,
    }));
  };

  // const onEligiblesSearch = (data: EligiblesDto) => {
  //   savePatientForm.firstName = data.firstName!;
  //   savePatientForm.lastName = data.lastName!;
  //   savePatientForm.dateOfBirth = data.dateOfBirth!;
  //   savePatientForm.email = data.email!;
  //   savePatientForm.phone = data.phoneNumber!;
  //   savePatientForm.zipcode = data.zipCode!;
  //   savePatientForm.contractId = data.contractId!;
  //   setSavePatientForm({ ...savePatientForm });
  // }

  return (
    <>
      {loading && <Spinner />}
      <Form
        className="d-flex flex-column h-100"
        noValidate
        validated={validated}
        onSubmit={submitHandler}
      >
        <SectionNavLayout>
            <Section title="Patient">
              <ReadField
                className="col-4"
                label="First Name"
                value={savePatientForm.firstName}
              />
              <ReadField
                className="col-4"
                label="Last Name"
                value={savePatientForm.lastName}
              />
              <div className="col-4">
                <label
                  htmlFor="middleName"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Preferred Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="middleName"
                  id="middleName"
                  placeholder="Preferred Name"
                  value={savePatientForm.middleName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">
                  Gender
                </label>
                <div className="d-flex align-items-center">
                  {genders.map((gender) => (
                    <div className="form-check me-3" key={gender.id}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="genderId"
                        id={`genderId-${gender.id}`}
                        value={gender.id} // Using gender id as the value
                        checked={savePatientForm.genderId === gender.id}
                        onChange={handleInputChange}
                        required
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`genderId-${gender.id}`}
                      >
                        {gender.name} {/* Displaying the gender type */}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <ReadField
                className="col-6"
                label="Date of Birth"
                value={formatDateTask(savePatientForm.dateOfBirth)}
              />
              <div className="col-6">
                <label
                  htmlFor="preferredLanguageId"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Preferred Language
                </label>
                <select
                  className="form-select"
                  id="preferredLanguageId"
                  name="preferredLanguageId"
                  value={savePatientForm.preferredLanguageId || ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select</option>
                  {languages.map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6">
                <label
                  htmlFor="raceId"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Race
                </label>
                <select
                  className="form-select"
                  id="raceId"
                  name="raceId"
                  value={savePatientForm.raceId || ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select</option>
                  {race.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6">
                <label
                  htmlFor="ethnicityId"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Ethnicity
                </label>
                <select
                  className="form-select"
                  id="ethnicityId"
                  name="ethnicityId"
                  value={savePatientForm.ethnicityId || ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select</option>
                  {ethinicity.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6">
                <label
                  htmlFor="RecurringOneTime"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Phone
                </label>
                <PhoneInput
                  name="phone"
                  value={savePatientForm.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-6">
                <label
                  htmlFor="RecurringOneTime"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={savePatientForm.email}
                  name="email"
                  onChange={handleInputChange}
                  required
                />
              </div>
            </Section>

            <Section title="Mailing Address">
              <div className="col-12">
                <label className="form-label fs-12 fw-semibold mb-1">Address Line</label>
                <input
                  type="text"
                  className="form-control lh-1"
                  id="streetAddress"
                  placeholder="Street Address"
                  value={savePatientForm.mailingAddress}
                  name="mailingAddress"
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">City</label>
                {cityList.length > 1 ? (
                  <select
                    className="form-select"
                    id="city"
                    name="cityId"
                    value={savePatientForm.cityId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select</option>
                    {cityList.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.value}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="form-control-plaintext">
                    {selectedCity ? selectedCity : <span className="text-light">City</span>}
                  </div>
                )}
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">State</label>
                <div className="form-control-plaintext">
                  {selectedState ? selectedState : <span className="text-light">State</span>}
                </div>
              </div>
              <div className="col-4">
                <label className="form-label fs-12 fw-semibold mb-1">Zipcode</label>
                <input
                  type="text"
                  className={`form-control lh-1 ${invalidZipCode && savePatientForm.zipcode !== "" ? "border-red" : ""}`}
                  id="zipcode"
                  placeholder="Zipcode"
                  value={savePatientForm.zipcode}
                  name="zipcode"
                  onChange={handleInputChange}
                  maxLength={5}
                  required
                />
                {invalidZipCode && savePatientForm.zipcode !== "" ? (
                  <small style={{ color: "red" }}>Invalid Zipcode</small>
                ) : (
                  ""
                )}
              </div>
            </Section>

            <Section title="Partner & Pathway">
              <div className="col-6">
                <label
                  htmlFor="RecurringOneTime"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Contract
                </label>
                <select
                  className="form-select"
                  id="RecurringOneTime"
                  value={savePatientForm.contractId}
                  name="contractId"
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Contract</option>
                  {contracts!.length > 0 &&
                    contracts!.map((type: Contracts, index: number) => (
                      <option key={index} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                </select>
              </div>
              <ReadField
                className="col-6"
                label="External Patient ID from Partner"
                value={eligiblePatient?.patientExternalIdFromPartner}
              />
              <ReadField
                className="col-12"
                label="Pathway"
                value={workflowStatesCache.get(savePatientForm.pathwayDesignerStateId || "")}
              />
            </Section>

            <Section title="Consent">
              <div className="col-12 fs-13">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="termsOfService"
                    name="termsOfService"
                    checked={savePatientForm.termsOfService}
                    onChange={handleCheckboxChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="termsOfService">
                    Terms Of Services{" "}
                    <a
                      className="a-link a-underline text-info fs-12 text-nowrap"
                      href="#"
                      onClick={() => handleOffCanvasShow()}
                    >
                      Read...
                    </a>
                  </label>
                </div>
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="privacyPolicy"
                    name="privacyPolicy"
                    checked={savePatientForm.privacyPolicy}
                    onChange={handleCheckboxChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="privacyPolicy">
                    Privacy Policy
                  </label>
                </div>
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="hipaaPrivacyPractices"
                    name="hipaaPrivacyPractices"
                    checked={savePatientForm.hipaaPrivacyPractices}
                    onChange={handleCheckboxChange}
                    required
                  />
                  <label
                    className="form-check-label"
                    htmlFor="hipaaPrivacyPractices"
                  >
                    HIPAA Privacy Practices
                  </label>
                </div>
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="consentOfCare"
                    name="consentOfCare"
                    checked={savePatientForm.consentOfCare}
                    onChange={handleCheckboxChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="consentOfCare">
                    Consent of Care
                  </label>
                </div>
              </div>
            </Section>

            <Section title="Additional Details">
              <div className="col-12">
                <label
                  htmlFor="RecurringOneTime"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  How did you hear about us?
                </label>
                <select
                  className="form-select"
                  id="RecurringOneTime"
                  value={savePatientForm.howHeard}
                  name="howHeard"
                  onChange={handleInputChange}
                >
                  <option value="">Select</option>
                  <option value="Email">Email</option>
                  <option value="Phone Call">Phone Call</option>
                  <option value="My Dr. or Clinic">My Dr. or Clinic</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {(savePatientForm.howHeard === "My Dr. or Clinic" ||
                savePatientForm.howHeard === "Other") && (
                <div className="col-12">
                  <label
                    htmlFor="howHeardDetails"
                    className="form-label fs-12 fw-semibold mb-1"
                  >
                    Please provide more details:
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="howHeardDetails"
                    placeholder="Enter details"
                    name="howHeardOther"
                    value={savePatientForm.howHeardOther}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              <div className="col-12">
                <label
                  htmlFor="notes"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Notes
                </label>
                <textarea
                  className="form-control"
                  id="notes"
                  rows={4}
                  placeholder="Write notes here"
                  name="notes"
                  value={savePatientForm.notes}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </Section>
        </SectionNavLayout>

        {/* Footer */}
        <div className="offcanvas-footer text-end w-100 border-top d-flex justify-content-between px-4 py-3">
          <div>
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill px-5 fs-13"
              type="reset"
              onClick={() => setOffCanvasShow(false)}
            >
              Discard
            </button>
          </div>
          <div>
            <button
              className="btn btn-sm btn-secondary rounded-pill px-5 fs-13"
              type="submit"
            >
              Enroll Patient
            </button>
          </div>
        </div>
      </Form>
      <ScriptModal
        show={modalShow}
        onHide={() => setModalShow(false)}
      ></ScriptModal>
    </>
  );
};

export default AddPatient;
